import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import ToolHelpButton from '../../../components/ToolHelpButton';
import { fmtDate } from '../../../utils/ruDates';
import { matchesSearch } from '../../../utils/search';

const METRIC_DEFS = [
    { key: 'openCount', label: 'Перешли в карточку', type: 'number', group: 'shows' },
    { key: 'cartCount', label: 'Положили в корзину', type: 'number', group: 'shows' },
    { key: 'orderCount', label: 'Заказано товаров', type: 'number', group: 'sales' },
    { key: 'orderSum', label: 'Заказано на сумму', type: 'number', group: 'sales' },
    { key: 'buyoutCount', label: 'Выкупили товаров', type: 'number', group: 'sales' },
    { key: 'buyoutSum', label: 'Выкупили на сумму', type: 'number', group: 'sales' },
    { key: 'cancelCount', label: 'Отмены/возвраты (шт)', type: 'number', group: 'sales' },
    { key: 'cancelSum', label: 'Отмены/возвраты (сумма)', type: 'number', group: 'sales' },
    { key: 'avgPrice', label: 'Средняя цена', type: 'number', group: 'sales' },
    { key: 'shareOrderPercent', label: 'Доля в выручке %', type: 'percent', group: 'sales' },
    { key: 'addToWishlist', label: 'Добавили в отложенные', type: 'number', group: 'shows' },
    { key: 'localizationPercent', label: 'Локальные заказы %', type: 'percent', group: 'sales' },
    { key: 'addToCartPercent', label: 'Конверсия в корзину %', type: 'percent', group: 'conversion' },
    { key: 'cartToOrderPercent', label: 'Конверсия в заказ %', type: 'percent', group: 'conversion' },
    { key: 'buyoutPercent', label: 'Выкуп %', type: 'percent', group: 'conversion' },
];

const GROUP_LABELS = {
    sales: 'Продажи',
    shows: 'Показы',
    conversion: 'Конверсии',
};

function WbStatistics({ accountId, projectMode = false, projectCabinets = [] }) {
    const { authFetch, API_BASE_URL } = useApi();
    const [periodFrom, setPeriodFrom] = useState('');
    const [periodTo, setPeriodTo] = useState('');
    const [rawItems, setRawItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [hideZeros, setHideZeros] = useState(true);
    const [sortField, setSortField] = useState('orderSum');
    const [sortDir, setSortDir] = useState('desc');
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [hiddenGroups, setHiddenGroups] = useState([]);
    // Раньше вся таблица (весь ассортимент кабинета) рендерилась одним `rows.map`
    // без пагинации — у крупных WB-продавцов с тысячами SKU это тормозило страницу.
    const PAGE_SIZE = 100;
    const [page, setPage] = useState(0);

    const visibleMetrics = useMemo(
        () => METRIC_DEFS.filter((m) => !hiddenColumns.includes(m.key) && !hiddenGroups.includes(m.group)),
        [hiddenColumns, hiddenGroups]
    );
    const accountIds = projectMode && projectCabinets.length > 0
        ? projectCabinets.map((c) => c.id)
        : accountId ? [accountId] : [];
    const accountIdsKey = useMemo(
        () => [...accountIds].map(String).sort().join(',') || 'none',
        [projectMode, projectCabinets, accountId]
    );
    const storageKey = `wb_statistics_state_${projectMode ? 'project' : 'account'}_${accountIdsKey}`;

    const applyPreset = (days) => {
        const to = new Date();
        to.setDate(to.getDate() - 1);
        const from = new Date(to);
        from.setDate(from.getDate() - (days - 1));
        const d = (x) => x.toISOString().split('T')[0];
        setPeriodFrom(d(from));
        setPeriodTo(d(to));
    };

    const toggleColumn = (key) => {
        setHiddenColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    };

    const toggleGroup = (group) => {
        setHiddenGroups((prev) => (prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]));
    };

    useEffect(() => {
        try {
            const rawState = localStorage.getItem(storageKey);
            if (!rawState) return;
            const saved = JSON.parse(rawState);
            setPeriodFrom(saved.periodFrom || '');
            setPeriodTo(saved.periodTo || '');
            setRawItems(Array.isArray(saved.rawItems) ? saved.rawItems : []);
            setSearch(saved.search || '');
            setHideZeros(saved.hideZeros ?? true);
            setSortField(saved.sortField || 'orderSum');
            setSortDir(saved.sortDir || 'desc');
            setHiddenColumns(Array.isArray(saved.hiddenColumns) ? saved.hiddenColumns : []);
            setHiddenGroups(Array.isArray(saved.hiddenGroups) ? saved.hiddenGroups : []);
        } catch (_) {
            // ignore broken local state
        }
    }, [storageKey]);

    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify({
                periodFrom,
                periodTo,
                rawItems,
                search,
                hideZeros,
                sortField,
                sortDir,
                hiddenColumns,
                hiddenGroups,
            }));
        } catch (_) {
            // ignore storage errors
        }
    }, [storageKey, periodFrom, periodTo, rawItems, search, hideZeros, sortField, sortDir, hiddenColumns, hiddenGroups]);

    const getMetricValue = (row, key) => {
        if (key === 'addToCartPercent' || key === 'cartToOrderPercent' || key === 'buyoutPercent') {
            return Number(row?.selected?.conversions?.[key] || 0);
        }
        return Number(row?.selected?.[key] || 0);
    };

    const loadData = async () => {
        if (!periodFrom || !periodTo) return alert('Укажите период');
        if (accountIds.length === 0) return;
        setLoading(true);
        try {
            const bySku = new Map();
            const limit = 1000;

            for (const currentAccountId of accountIds) {
                let offset = 0;
                while (true) {
                    const data = await authFetch(`${API_BASE_URL}/api/v1/wb-analytics/${currentAccountId}/products`, {
                        method: 'POST',
                        body: JSON.stringify({
                            selectedPeriod: { start: periodFrom, end: periodTo },
                            limit,
                            offset,
                            orderBy: { field: 'orderSum', mode: 'desc' },
                            nmIds: [],
                            brandNames: [],
                            subjectIds: [],
                            tagIds: [],
                            skipDeletedNm: false,
                        }),
                    });
                    const items = data?.items || [];
                    for (const row of items) {
                        const p = row.product || {};
                        const sku = String(p.nmId || '');
                        const existing = bySku.get(sku) || {
                            product: { ...p },
                            selected: {
                                openCount: 0,
                                cartCount: 0,
                                orderCount: 0,
                                orderSum: 0,
                                buyoutCount: 0,
                                buyoutSum: 0,
                                cancelCount: 0,
                                cancelSum: 0,
                                avgPrice: 0,
                                shareOrderPercent: 0,
                                addToWishlist: 0,
                                localizationPercent: 0,
                                conversions: { addToCartPercent: 0, cartToOrderPercent: 0, buyoutPercent: 0 },
                            },
                            sourceAccountIds: [],
                        };
                        if (!existing.sourceAccountIds.includes(currentAccountId)) existing.sourceAccountIds.push(currentAccountId);
                        for (const key of ['openCount', 'cartCount', 'orderCount', 'orderSum', 'buyoutCount', 'buyoutSum', 'cancelCount', 'cancelSum', 'addToWishlist', 'shareOrderPercent', 'localizationPercent']) {
                            existing.selected[key] += Number(row?.selected?.[key] || 0);
                        }
                        existing.selected.avgPrice = existing.selected.orderCount > 0
                            ? existing.selected.orderSum / existing.selected.orderCount
                            : 0;
                        existing.selected.conversions = {
                            addToCartPercent: existing.selected.openCount > 0 ? (existing.selected.cartCount / existing.selected.openCount) * 100 : 0,
                            cartToOrderPercent: existing.selected.cartCount > 0 ? (existing.selected.orderCount / existing.selected.cartCount) * 100 : 0,
                            buyoutPercent: existing.selected.orderCount > 0 ? (existing.selected.buyoutCount / existing.selected.orderCount) * 100 : 0,
                        };
                        bySku.set(sku, existing);
                    }
                    if (items.length < limit) break;
                    offset += limit;
                }
            }
            setRawItems(Array.from(bySku.values()));
        } catch (e) {
            alert(`Ошибка загрузки: ${e.message}`);
            setRawItems([]);
        } finally {
            setLoading(false);
        }
    };

    const rows = useMemo(() => {
        let out = [...rawItems];

        if (search.trim()) {
            out = out.filter((row) => matchesSearch(row, search));
        }

        if (hideZeros) {
            out = out.filter((row) => visibleMetrics.some((m) => getMetricValue(row, m.key) !== 0));
        }

        out.sort((a, b) => {
            let av;
            let bv;
            if (sortField === 'nmId') {
                av = String(a.product?.nmId || '');
                bv = String(b.product?.nmId || '');
            } else if (sortField === 'vendorCode') {
                av = String(a.product?.vendorCode || '');
                bv = String(b.product?.vendorCode || '');
            } else if (sortField === 'title') {
                av = String(a.product?.title || '');
                bv = String(b.product?.title || '');
            } else {
                av = getMetricValue(a, sortField);
                bv = getMetricValue(b, sortField);
            }
            if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(String(bv || ''), 'ru') : String(bv || '').localeCompare(av, 'ru');
            const an = Number(av);
            const bn = Number(bv);
            const avNum = Number.isFinite(an) ? an : 0;
            const bvNum = Number.isFinite(bn) ? bn : 0;
            if (avNum === bvNum) {
                return String(a.product?.nmId || '').localeCompare(String(b.product?.nmId || ''), 'ru');
            }
            return sortDir === 'asc' ? avNum - bvNum : bvNum - avNum;
        });
        return out;
    }, [rawItems, search, hideZeros, sortField, sortDir, visibleMetrics]);

    const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const pagedRows = useMemo(
        () => rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
        [rows, page]
    );
    useEffect(() => {
        setPage(0);
    }, [search, hideZeros, rawItems]);

    const totals = useMemo(() => {
        if (rows.length === 0) return null;
        const result = {};
        for (const def of METRIC_DEFS) {
            let sum = 0;
            for (const row of rows) sum += getMetricValue(row, def.key);
            result[def.key] = sum;
        }
        if (result.orderCount > 0) result.avgPrice = result.orderSum / result.orderCount;
        return result;
    }, [rows]);

    const onSort = (field) => {
        if (sortField === field) setSortDir((v) => (v === 'asc' ? 'desc' : 'asc'));
        else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    const num = (v) => (v === null || v === undefined ? '-' : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v));
    const pct = (v) => (v === null || v === undefined ? '-' : `${num(v)}%`);
    const val = (v, type) => (type === 'percent' ? pct(v) : num(v));
    const d = (x) => (x ? fmtDate(x) : '-');
    const SI = ({ field }) => <span className={`sort-indicator ${sortField === field ? 'active' : ''}`}>{sortField !== field ? '⇅' : sortDir === 'asc' ? '↑' : '↓'}</span>;

    return (
        <div className="ozon-analytics-panel">
            <div className="analytics-controls">
                <div className="analytics-periods">
                    <div className="period-row">
                        <label className="period-label">Период:</label>
                        <input type="date" className="period-input" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
                        <span className="period-separator">—</span>
                        <input type="date" className="period-input" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
                    </div>
                </div>
                <div className="analytics-presets">
                    <span className="presets-label">Быстрый выбор:</span>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset(7)}>Неделя</button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset(30)}>Месяц</button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset(90)}>Квартал</button>
                </div>
                <div className="analytics-actions">
                    <button type="button" className="btn btn-primary" onClick={loadData} disabled={loading || accountIds.length === 0}>
                        {loading ? 'Загрузка...' : 'Загрузить'}
                    </button>
                </div>
            </div>

            {rawItems.length > 0 && (
                <div className="analytics-filters">
                    <div className="filter-row">
                        <div className="filter-group">
                            <label className="filter-checkbox">
                                <input type="checkbox" checked={hideZeros} onChange={(e) => setHideZeros(e.target.checked)} />
                                Скрыть нулевые
                            </label>
                        </div>
                        <div className="filter-search">
                            <input
                                type="text"
                                className="filter-input"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Поиск по названию, артикулу, SKU..."
                            />
                        </div>
                    </div>
                    <div className="filter-row" style={{ marginTop: 8 }}>
                        <div className="filter-group">
                            <span className="presets-label">Скрыть группы:</span>
                            {Object.keys(GROUP_LABELS).map((group) => (
                                <label key={group} className="filter-checkbox">
                                    <input type="checkbox" checked={hiddenGroups.includes(group)} onChange={() => toggleGroup(group)} />
                                    {GROUP_LABELS[group]}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="filter-row" style={{ marginTop: 8 }}>
                        <div className="filter-group" style={{ flexWrap: 'wrap' }}>
                            <span className="presets-label">Скрыть колонки:</span>
                            {METRIC_DEFS.map((m) => (
                                <label key={m.key} className="filter-checkbox">
                                    <input type="checkbox" checked={hiddenColumns.includes(m.key)} onChange={() => toggleColumn(m.key)} />
                                    {m.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {rawItems.length > 0 && totals && (
                <>
                    <div className="analytics-summary">
                        <h3>Общая статистика<ToolHelpButton toolKey="wb_statistics" /></h3>
                        <table className="summary-table">
                            <thead>
                                <tr>
                                    <th>Период</th>
                                    {visibleMetrics.map((m) => <th key={m.key} className="text-right">{m.label}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{d(periodFrom)} — {d(periodTo)}</td>
                                    {visibleMetrics.map((m) => (
                                        <td key={m.key} className="text-right">{val(totals[m.key], m.type)}</td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="analytics-detail">
                        <h3>Детализация по товарам ({rows.length})</h3>
                        <div className="analytics-table-wrapper">
                            <table className="analytics-table">
                                <thead>
                                    <tr>
                                        <th className="sticky-col col-article clickable" onClick={() => onSort('vendorCode')}>Артикул <SI field="vendorCode" /></th>
                                        <th className="sticky-col col-sku clickable" onClick={() => onSort('nmId')}>SKU <SI field="nmId" /></th>
                                        <th className="col-name clickable" onClick={() => onSort('title')}>Название <SI field="title" /></th>
                                        {visibleMetrics.map((m) => (
                                            <th key={m.key} className="col-metric clickable" onClick={() => onSort(m.key)}>
                                                {m.label} <SI field={m.key} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedRows.map((row) => {
                                        const p = row.product || {};
                                        return (
                                            <tr key={String(p.nmId)}>
                                                <td className="sticky-col col-article">{p.vendorCode || '-'}</td>
                                                <td className="sticky-col col-sku">
                                                    <a className="sku-link" href={`https://www.wildberries.ru/catalog/${p.nmId}/detail.aspx`} target="_blank" rel="noreferrer">
                                                        {p.nmId}
                                                    </a>
                                                </td>
                                                <td className="col-name"><span className="cell-truncate">{p.title || '-'}</span></td>
                                                {visibleMetrics.map((m) => (
                                                    <td key={`${p.nmId}-${m.key}`} className="col-metric text-right">{val(getMetricValue(row, m.key), m.type)}</td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {pageCount > 1 && (
                            <div className="row-wrap" style={{ justifyContent: 'center', gap: 16, marginTop: 8 }}>
                                <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>← Назад</button>
                                <span className="muted">Страница {page + 1} из {pageCount}</span>
                                <button type="button" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}>Вперёд →</button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default WbStatistics;
